import { Outlet, redirect } from 'react-router';

import type { Route } from './+types/themed-layout';
import { guardThemedShell } from '@/store/guards';
import { useUserStore } from '@/store/user';
import { themeFromPathname } from '@/utils/theme';

/**
 * Shell for every `/h/:theme` screen. It validates the theme segment on the
 * server and enforces the onboarding guards on the client — the profile lives
 * in `localStorage`, so only the browser can decide who this is.
 *
 * The `dark` class itself is applied in `root.tsx`, because React Router has a
 * single document.
 */
export function loader({ request }: Route.LoaderArgs) {
  const theme = themeFromPathname(new URL(request.url).pathname);

  if (theme === null) {
    throw redirect('/');
  }

  return { theme };
}

export async function clientLoader({ serverLoader }: Route.ClientLoaderArgs) {
  const { theme } = await serverLoader();

  const guard = guardThemedShell({
    profile: useUserStore.getState().profile,
    theme,
  });

  if (guard) {
    throw redirect(guard.to);
  }

  return { theme };
}

// Runs on a hard refresh too, so a profile-less visitor never sees a themed
// screen — not even for a frame.
clientLoader.hydrate = true as const;

/** Invisible on purpose: the guard resolves before anything paints. */
export function HydrateFallback() {
  return null;
}

export default function ThemedLayout() {
  return <Outlet />;
}
